import BasePropertyView from 'BasePropertyView.js';
import {Model} from 'backbone';
import listService  from 'ListService.js';
import util from 'util/Util.js';
import sequenceService from 'SequenceService.js';
import undoService from 'UndoService.js';
import selectFieldModal from 'common-views/SelectFieldModalView';
import formulaModal from 'common-views/MainFormulaModalView.js';
import formulaService from 'xml/FormulaService.js';
import Template from 'behaviors/properties/main/condition.ejs';
import textConstants from 'TextConstants';

//=====================================================================
//
// An instance of this view represents a condition.
//
// Casey Justus, April 21, 2016
//
//=====================================================================

class MainConditionView extends BasePropertyView {

    initialize( opts ) {
        this.options = opts || {};
        this.template = Template;
        this.tagName = 'div';
        this.id = opts.el;
        this.model.set({conditionDisabled: ''}, {silent:true});
        this.tempSimpleCondition;
        this.tempAdvancedCondition;
        this.model.set({radioSimpleClick: false}, {silent:true});        

        this.events = {
            'change select'                 : 'updateAttributes',
            'change input[type=radio]'      : 'updateRadioAttributes',
            'change input[type=text]'       : 'updateAttributes',
            'blur input[type=text]'         : 'lostFocus',
            'focus input[type=text]'        : 'getFocus',
            'click #selectField'            : 'showValueFieldModal',
            'click #advEdit'                : 'showFormulaModal'
        };

        if (!this.model.get('conditionDisabled')) {
            this.model.set('conditionDisabled', "", {silent: true});
        }

        if (!this.model.get('conditionType')) {
            if (this.model.attributes.factoryKey === 'command_simple_branch'){
                this.model.set('conditionType','simple', {silent:true});
            }
            else {
                this.model.set('conditionType','always', {silent:true});
            }
        }
        if (!this.model.get('conditionReadOnly')) {
            this.model.set('conditionReadOnly', "", {silent: true});
        }

        if (!this.model.get('secondCondition')) {
            this.model.set('secondCondition', "", {silent: true});
        }

        this.model.set('fieldName', '', {silent: true});
        this.model.set('conditionSelect', '=', {silent: true});
        this.model.set('secondCondition', '', {silent: true});

        this.displayUI = true;
        this.isSourceMultiBranch = false;
        if (this.model.get('srcWidgetModel')) {
            // Note PF-4903 - srcWidget and destWidget models were reversed by ListService clode op within  in this.model after copy-paste
            this.isSourceMultiBranch = this.model.get('srcWidgetModel').attributes.factoryKey === 'command_multiple_branch';
        }

        if (this.model.get('factoryKey') === 'connection_arrow' ) {
            this.displayUI = false;
            if ( listService.mainKind === 'W' || this.isSourceMultiBranch ) {
                this.displayUI = true;
                if (!this.model.get('conditionType')) {
                    this.model.set('conditionType', 'always', {silent:true});
                }
            }
        }

        if (this.displayUI) {
            let parmTest = this.getParameter();
            if ( !parmTest && this.model.get('conditionType') !== 'always' && this.model.get('factoryKey'!== 'command_simple_branch') ) {
                this.createInitialParameter(true);
            }

            this.loadFromParameter();
            this.listenTo(this.model, 'change', this.render);
        }

        return this;
    }

    createInitialParameter(simple) {
        let Parameter = {id: 'default', type: 'expression', literal: 'null', field: 'null', special: 'null'};
        Parameter.Expression = {id: sequenceService.nextDomId(), type: 'scriptingCondition'};
        // noinspection JSAnnotator
        Parameter.Expression.arguments = [];
        if (simple) {
            // noinspection JSAnnotator
            Parameter.Expression.arguments.push({
                id: sequenceService.nextDomId(),
                kind: 'argument',
                type: 'scripting_formField',
                fieldId: '',
                value: ''
            });
            // noinspection JSAnnotator
            Parameter.Expression.arguments.push({
                id: sequenceService.nextDomId(),
                kind: 'operator',
                type: '==',
                fieldId: '',
                value: '='
            });
            // noinspection JSAnnotator
            Parameter.Expression.arguments.push({
                id: sequenceService.nextDomId(),
                kind: 'argument',
                type: 'scripting_literal',
                fieldId: '',
                value: ''
            });
        }

        this.model.attributes.Parameters = {};
        this.model.attributes.Parameters.Parameter = [];
        this.model.attributes.Parameters.Parameter.push(Parameter);

        return Parameter;
    }

    isSimpleCondition(expression) {
        // PF-4074 -- 3rd arg tested for being a scripting_literal but a scripting_boolean is a literal also. Allow scripting_boolean.
        // Also, we don't need nested ifs (boolean tests) to an evaluated boolean.
        return expression.arguments &&
            expression.arguments.length === 3 &&
            expression.arguments[0].type === 'scripting_formField' &&
            expression.arguments[1].kind === 'operator' &&
            (
                expression.arguments[2].type === 'scripting_literal' ||
                expression.arguments[2].type === 'scripting_boolean'
            );
    }

    loadFromParameter() {
        const parm = this.getParameter();


        if (parm && parm.Expression) {
            // determine if it is Simple or Advanced based on Pattern
            // Simple will have arg-op-arg , if it doesn't match then it's advanced
            // arg - type = scripting_formField
            // op -
            // arg - type = scripting_literal ( PF-4071 or scripting_boolean)

            if (this.isSimpleCondition(parm.Expression) ) {
                const objName = listService.determineFieldName(parm.Expression.arguments[0].fieldId);
                //PF-6666 The field name could be empty in case the field does not exists anymore but it should not be undefined
                if (typeof objName !== 'undefined') {
                    parm.Expression.arguments[0].value = objName;
                }

                this.model.set('fieldName', parm.Expression.arguments[0].value, {silent: true});
                this.model.set('conditionSelect', parm.Expression.arguments[1].value, {silent: true});
                this.model.set('secondCondition', parm.Expression.arguments[2].value, {silent: true});

                // override conditionSelect if boolean special case
                if (parm.Expression.arguments[1].value === '=' && parm.Expression.arguments[2].type === 'scripting_boolean') {
                    if (parm.Expression.arguments[2].value === 'true') {
                        this.model.set('conditionSelect', 'checked', {silent: true});
                    } else {
                        this.model.set('conditionSelect', 'notchecked', {silent: true});
                    }
                }

                this.tempSimpleCondition = parm.Expression;

                if (this.model.get('conditionSelect') === 'empty' || this.model.get('conditionSelect') === 'notempty' ||
                    this.model.get('conditionSelect') === 'checked' || this.model.get('conditionSelect') === 'notchecked') {

                    this.model.set('secondCondition', '', {silent: true});
                }

                this.model.set('conditionType','simple', {silent:true});
                this.model.set({conditionDisabled: ''}, {silent: true});
                this.model.set({conditionReadOnly: ''}, {silent: true});
            } else {
                this.tempAdvancedCondition = parm.Expression;

                this.model.set('conditionType','advanced', {silent:true});
                this.model.set({conditionDisabled: 'disabled'}, {silent: true});
                this.model.set({conditionReadOnly: 'readonly'}, {silent: true});
            }


        }

    }

    getParameter() {
        let self = this;
        let parameter = undefined;

        const prop = this.model.get('Parameters');
        if (prop) {
            parameter = _.find(prop.Parameter, function (obj) {
                return obj.id === 'default'
            });
        }
        return parameter;
    }

    updateAttributes(ev) {
        const propName = ev.currentTarget.name;
        const propVal  = ev.currentTarget.value;
        const oldPropVal  = this.model.get(propName);
        let oldArgumentValue,oldArgumentType;

        this.model.set( propName, propVal, {silent:true} );
        let oldParameters=Object.assign({},this.model.get('Parameters'));
        let parm =  this.getParameter();

        if (propName === 'conditionSelect') {
            if (parm.Expression && parm.Expression.arguments[1]) {
                oldArgumentValue = parm.Expression.arguments[1].value;
                oldArgumentType = parm.Expression.arguments[1].type;
            }

            // PF-4074 conditional boolean override of normal literal
            // On 'conditionSelect' propval: if  propVal is checked or unchecked, set item 2 (3rd item) kind to 'scripting_boolean' instead of 'scripting_literal'
            if (new Set(['checked', 'notchecked']).has(propVal)) {
                // Scripting bootean special case
                parm.Expression.arguments[1].value = '=';
                parm.Expression.arguments[1].type = '==';
                parm.Expression.arguments[2].type = 'scripting_boolean';
                parm.Expression.arguments[2].value = propVal ==='checked' ? 'true' : 'false';
            } else {
                // Scripting literal case is normal
                parm.Expression.arguments[1].value = propVal;
                parm.Expression.arguments[1].type = ev.currentTarget.selectedOptions[0].attributes["data-type"].value;
            }

        }

        if (propName === 'secondCondition') {
            if(parm.Expression && parm.Expression.arguments[2]) {
                oldArgumentValue = parm.Expression.arguments[2].value;
                oldArgumentType = parm.Expression.arguments[2].type;
            }
            parm.Expression.arguments[2].value = propVal;
            parm.Expression.arguments[2].type = 'scripting_literal';
        }

        this.setFormulaText(parm.Expression);
        this.model.trigger('change');
        let updatedParameters=Object.assign({},this.model.get('Parameters'));

        undoService.add({
            undo:() =>{
                this.model.set( propName, oldPropVal, {silent:true} );
                this.model.set('Parameters',oldParameters,{silent:true});
                let undoParm=this.getParameter();
                if (propName === 'conditionSelect') {
                    undoParm.Expression.arguments[1].value = oldArgumentValue;
                    undoParm.Expression.arguments[1].type = oldArgumentType;
                }
                if (propName === 'secondCondition') {
                    undoParm.Expression.arguments[2].value = oldArgumentValue;
                    undoParm.Expression.arguments[2].type = oldArgumentType;
                }
                this.setFormulaText(undoParm.Expression);
                this.model.trigger('change');
            },
            redo:() =>{
                this.model.set( propName, propVal, {silent:true} );
                this.model.set('Parameters',updatedParameters,{silent:true});
                let redoParm=this.getParameter();
                if (propName === 'conditionSelect') {
                    redoParm.Expression.arguments[1].value = propVal;
                    redoParm.Expression.arguments[1].type = ev.currentTarget.selectedOptions[0].attributes["data-type"].value;
                }
                if (propName === 'secondCondition') {
                    redoParm.Expression.arguments[2].value = propVal;
                    // Question: what if this is a boolean? E.g checked or notchecked?
                    redoParm.Expression.arguments[2].type = 'scripting_literal';
                }
                this.setFormulaText(redoParm.Expression);
                this.model.trigger('change');
            }
        },textConstants.UNDO_PROP_CHANGE)

    }

    updateRadioAttributes(ev) {       
        const propName = ev.currentTarget.name;
        const propVal  = ev.currentTarget.value;
        const oldPropVal = this.model.get(propName);
        let oldRadioSimpleClick = this.model.get('radioSimpleClick');
        let oldTempSimpleCondition = this.tempSimpleCondition;
        let oldTempAdvancedCondition = this.tempAdvancedCondition;
        let oldConditionDisabled = this.model.get('conditionDisabled');
        let oldConditionReadOnly = this.model.get('conditionReadOnly');
        let oldHasConditions = this.model.get('hasConditions');
        let oldFormulaText = this.model.get('formulaText');
        let oldModelParameters = Object.assign({},this.model.get('Parameters'));




        if (propVal === 'simple'){
            this.model.set({radioSimpleClick: true}, {silent:true}); 
        }

       let parm = this.getParameter();

        //cache types of conditions
        const currentType = this.model.get('conditionType');
        switch (currentType) {
            case 'simple':
                this.tempSimpleCondition = parm? parm.Expression : undefined;
                break;
            case 'advanced':
                this.tempAdvancedCondition = parm? parm.Expression: undefined;
                break;
            case 'always':
                break;
        }

        this.model.set( propName, propVal, {silent:true} );

        switch (propVal) {
            case 'simple':
                if (!parm) {
                    parm = this.createInitialParameter(true);
                    if (this.tempSimpleCondition) {
                        parm.Expression = this.tempSimpleCondition;
                    }
                } else {
                    if (!this.tempSimpleCondition) {
                        parm = this.createInitialParameter(true);
                    } else {
                        parm.Expression = this.tempSimpleCondition;
                    }
                }

                $('#advEdit').prop('disabled','disabled');
                this.model.set({conditionDisabled: ''}, {silent:true});
                this.model.set({conditionReadOnly: ''}, {silent:true});
                this.setFormulaText(parm.Expression);
                this.model.set('hasConditions',  !util.isEmpty(this.model.get('fieldName')), {silent:true});
                break;
            case 'advanced':
                if (!parm) {
                    parm = this.createInitialParameter(false);
                }
                parm.Expression = (this.tempAdvancedCondition) ? this.tempAdvancedCondition : {};

                $('#advEdit').prop('disabled','');
                this.model.set({conditionDisabled: 'disabled'}, {silent:true});
                this.model.set({conditionReadOnly: 'readonly'}, {silent:true});
                this.setFormulaText(parm.Expression);
                this.model.set('hasConditions', true, {silent: true});
                break;
            case 'always':
                this.model.attributes.Parameters = undefined;
                $('#advEdit').prop('disabled','disabled');
                this.model.set({conditionDisabled: 'disabled'}, {silent:true});
                this.model.set({conditionReadOnly: 'readonly'}, {silent:true});
                this.model.set('formulaText', '', {silent:true});
                this.model.set('hasConditions', false, {silent: true});
                break;
        }

        listService.fcanvas.renderAll();
        this.model.trigger('change');

        let updatedRadioSimpleClick = this.model.get('radioSimpleClick');
        let updatedTempSimpleCondition = this.tempSimpleCondition;
        let updatedTempAdvancedCondition = this.tempAdvancedCondition;
        let updatedConditionDisabled = this.model.get('conditionDisabled');
        let updatedConditionReadOnly = this.model.get('conditionReadOnly');
        let updatedHasCondition = this.model.get('hasConditions');
        let updatedFormulaText = this.model.get('formulaText');
        let updatedModelParameters = Object.assign({},this.model.get('Parameters'));



        undoService.add({
            undo:() => {
                this.model.set('Parameters',oldModelParameters,{silent:true});
                let undoParm = this.getParameter();
                this.model.set('radioSimpleClick',oldRadioSimpleClick,{silent:true});
                this.tempSimpleCondition = oldTempSimpleCondition;
                this.tempAdvancedCondition = oldTempAdvancedCondition;
                this.model.set(propName,oldPropVal,{silent:true});

                switch (oldPropVal){
                    case 'simple':
                        $('#advEdit').prop('disabled',false);
                        this.model.set({conditionDisabled: oldConditionDisabled}, {silent:true});
                        this.model.set({conditionReadOnly: oldConditionReadOnly}, {silent:true});
                        this.setFormulaText(undoParm.Expression);
                        this.model.set('hasConditions',  oldHasConditions, {silent:true});
                        break;
                    case 'advanced':
                        $('#advEdit').prop('disabled','disabled');
                        this.model.set({conditionDisabled: oldConditionDisabled}, {silent:true});
                        this.model.set({conditionReadOnly: oldConditionReadOnly}, {silent:true});
                        this.setFormulaText(undoParm.Expression);
                        this.model.set('hasConditions', oldHasConditions, {silent: true});
                        break;
                    case 'always':
                        this.model.attributes.Parameters = oldModelParameters;
                        $('#advEdit').prop('disabled',false);
                        this.model.set({conditionDisabled: oldConditionDisabled}, {silent:true});
                        this.model.set({conditionReadOnly: oldConditionReadOnly}, {silent:true});
                        this.model.set('formulaText', oldFormulaText, {silent:true});
                        this.model.set('hasConditions', oldHasConditions, {silent: true});
                        break;
                }
                listService.fcanvas.renderAll();
                this.model.trigger('change');
            },
            redo:() => {
                this.model.set('Parameters',updatedModelParameters,{silent:true});
                let redoParm = this.getParameter();
                this.model.set('radioSimpleClick',updatedRadioSimpleClick,{silent:true});
                this.tempSimpleCondition = updatedTempSimpleCondition;
                this.tempAdvancedCondition = updatedTempAdvancedCondition;
                this.model.set(propName,propVal,{silent:true});

                switch (propVal){
                    case 'simple':
                        $('#advEdit').prop('disabled','disabled');
                        this.model.set({conditionDisabled: updatedConditionDisabled}, {silent:true});
                        this.model.set({conditionReadOnly: updatedConditionReadOnly}, {silent:true});
                        this.setFormulaText(redoParm.Expression);
                        this.model.set('hasConditions',  updatedHasCondition, {silent:true});
                        break;
                    case 'advanced':
                        $('#advEdit').prop('disabled',false);
                        this.model.set({conditionDisabled: updatedConditionDisabled}, {silent:true});
                        this.model.set({conditionReadOnly: updatedConditionReadOnly}, {silent:true});
                        this.setFormulaText(redoParm.Expression);
                        this.model.set('hasConditions', updatedHasCondition, {silent: true});
                        break;
                    case 'always':
                        this.model.attributes.Parameters = updatedModelParameters;
                        $('#advEdit').prop('disabled',false);
                        this.model.set({conditionDisabled: updatedConditionDisabled}, {silent:true});
                        this.model.set({conditionReadOnly: updatedConditionReadOnly}, {silent:true});
                        this.model.set('formulaText', updatedFormulaText, {silent:true});
                        this.model.set('hasConditions', updatedHasCondition, {silent: true});
                        break;
                }
                listService.fcanvas.renderAll();
                this.model.trigger('change');
            }
        },textConstants.UNDO_EDIT_VALUE)
    }

    showValueFieldModal(ev) {
        let self = this;
        ev.preventDefault();

        var sfModel = new Model();
        var formulaSelectedValue = this.model.get("fieldName");
        var formulaSelectedItem = undefined;
        
        if(formulaSelectedValue && this.model.attributes.Parameters 
            && this.model.attributes.Parameters.Parameter
            && this.model.attributes.Parameters.Parameter.length > 0 
            && this.model.attributes.Parameters.Parameter[0].Expression
            && this.model.attributes.Parameters.Parameter[0].Expression.arguments
            && this.model.attributes.Parameters.Parameter[0].Expression.arguments.length > 0)
        {
            formulaSelectedItem = this.model.attributes.Parameters.Parameter[0].Expression.arguments
            .filter(x=> x.value == formulaSelectedValue);
        }
        
        if(formulaSelectedItem && formulaSelectedItem.length && formulaSelectedItem.length > 0)
        {
            if(formulaSelectedItem[0].fieldId)
            {
                sfModel.set("dataSelectedItemId", formulaSelectedItem[0].fieldId, {silent:true});
            }
            else
            {
                sfModel.set("dataSelectedItemId", "", {silent:true});
            }
        }

        const sfModal = new selectFieldModal({model: sfModel});
        sfModal.render().then((selectedField)=>{
            let oldFieldName= this.model.get('fieldName');
            let oldHasConditions= this.model.get('hasConditions');
            // used only for display in the UI
            this.model.set('fieldName', selectedField.name,{silent:true});
            // something with FieldID
            if (!this.getParameter()) {
                this.createInitialParameter(true);
            }
            let oldParameters = Object.assign({},this.model.get('Parameters'))
            let parm = this.getParameter();

            parm.Expression.arguments[0].fieldId = selectedField.id;
            parm.Expression.arguments[0].value = selectedField.name;
            self.setFormulaText(parm.Expression);
            this.model.set('hasConditions',  true, {silent:true});
            listService.fcanvas.renderAll();
            this.model.trigger('change');

            if(this.isSourceMultiBranch){
                const behavior = this.model.get("destWidgetModel");
                behavior.trigger("change", this.model.get("Parameters"));
            }

            let updatedParameters = Object.assign({},this.model.get('Parameters'))
            let updatedFieldName = this.model.get('fieldName');
            let updatedHasConditions = this.model.get('hasConditions');
            undoService.add({
                undo:() => {
                    this.model.set('Parameters',oldParameters,{silent:true});
                    let undoParm = this.getParameter();
                    this.model.set('fieldName',oldFieldName,{silent:true});
                    this.model.set('hasConditions',oldHasConditions,{silent:true});
                    self.setFormulaText(undoParm.Expression);
                    listService.fcanvas.renderAll();
                    this.model.trigger('change');
                },
                redo:() => {
                    this.model.set('Parameters',updatedParameters,{silent:true});
                    let redoParm = this.getParameter();
                    this.model.set('fieldName',updatedFieldName,{silent:true});
                    this.model.set('hasConditions',updatedHasConditions,{silent:true});
                    self.setFormulaText(redoParm.Expression);
                    listService.fcanvas.renderAll();
                    this.model.trigger('change');
                }

            },textConstants.UNDO_EDIT_VALUE)

        }).catch((err) => {
           });
    }

    showFormulaModal(ev) {
        ev.preventDefault();
        let attributes = {formulaParameter: this.model.get('formulaParameter'), title: 'Edit Condition'};
        if(!attributes.formulaParameter && this.model.get('factoryKey') === 'connection_arrow' ){
            attributes.formulaParameter = this.model.attributes.Parameters.Parameter[0].Expression;
        }

        var sfModal = new formulaModal({model: new Model(attributes), config: {selected: ''}});
        sfModal.render().then((formulaParameter)=>{
            let oldFormulaParameter;
            formulaParameter.type = 'scriptingCondition';
            if(this.model.get('factoryKey') === 'command_simple_branch'){
                oldFormulaParameter=this.model.get('formulaParameter');
                this.model.set('formulaParameter', formulaParameter, {silent: true});
            }else{
                oldFormulaParameter=this.model.attributes.Parameters.Parameter[0].Expression;
                this.model.attributes.Parameters.Parameter[0].Expression = formulaParameter;
            }
            this.setFormulaText(formulaParameter);

            undoService.add({
                undo:()=>{
                    if(this.model.get('factoryKey') === 'command_simple_branch'){
                        this.model.set('formulaParameter', oldFormulaParameter, {silent: true});
                    }else{
                        this.model.attributes.Parameters.Parameter[0].Expression = oldFormulaParameter;
                    }
                    this.setFormulaText(oldFormulaParameter);
                },
                redo: () =>{
                    if(this.model.get('factoryKey') === 'command_simple_branch'){
                        this.model.set('formulaParameter', formulaParameter, {silent: true});
                    }else{
                        this.model.attributes.Parameters.Parameter[0].Expression = formulaParameter;
                    }
                    this.setFormulaText(formulaParameter);
                }
            })
        }).catch(err => console.log(err));
    }

    setFormulaText(expression){
        const formulaText = formulaService.convertExpressionToText(expression);
        if (this.model.get('factoryKey') === 'connection_arrow') {
            this.model.set('formulaText', formulaText,{silent:true});
            this.model.trigger('change');
            listService.fcanvas.renderAll();
        } else {
            this.model.set('widgetText', formulaText,{silent:true});
            this.model.trigger('change');
        }
    }

    render() {
        if (this.model.attributes.hasConditions === undefined){
           //false for always  
           this.model.set('hasConditions', false, {silent: true});  
           if (this.model.attributes.Parameters){  
               if (!this.model.attributes.Parameters.Parameter[0].EXPR.EXPR){  
               //true for advanced  
                this.model.set('hasConditions', true, {silent: true});  
               }else if (this.model.attributes.Parameters.Parameter[0].Expression.arguments[0].value !== ''){  
            //true for simple (Text or Number)  
            this.model.set('hasConditions', true, {silent: true});
              }  
           } 
       }

        if (this.model.get('conditionType') === 'simple' && !this.model.get('hasConditions') && !this.model.get('radioSimpleClick')) {
            if (this.model.attributes.factoryKey === 'command_simple_branch'){
               this.model.set('conditionType', 'simple', {silent: true});
            }else{
               this.model.set('conditionType', 'always', {silent: true});  
            }
            this.model.set({radioSimpleClick: false}, {silent:true}); 
        }
        // Don't render this is if we are a connection arrow and NOT coming from a multi-branch
        if (this.model.get('factoryKey') === 'connection_arrow' && ( listService.mainKind === 'W' || this.isSourceMultiBranch )) {
            this.displayUI = true;
        }

        let conditionTitle = '';
        let conditionLabel = '';
        switch (this.model.get('factoryKey')) {
            case "connection_arrow":
                conditionTitle = this.isSourceMultiBranch ? 'Branch Condition' : 'Path Settings';
                conditionLabel = this.isSourceMultiBranch ? 'Text or number' : 'Simple';
                break;
            case "command_simple_branch":
                conditionTitle = 'Simple Branch Condition';
                conditionLabel =  'Simple';
                break;
            default:
                conditionTitle = 'Condition';
                conditionLabel = 'Text or number';
        }
        this.model.set('conditionTitle', conditionTitle, {silent:true});
        this.model.set('conditionLabel', conditionLabel, {silent:true});

        if (this.displayUI) {
            if (this.model.get('conditionType') === 'simple') {
                this.model.set({conditionDisabled: ''}, {silent: true});
            } else {
                this.model.set({conditionDisabled: 'disabled'}, {silent: true});
            }

            if (this.template) {
                this.$el.html(this.template(this.model.toJSON()));
            }

            if (listService.mainKind === 'W' || this.isSourceMultiBranch) {
                $('#conditionAlways').removeClass('hide');
            }

            $("input:radio[value='" + this.model.get('conditionType') + "']").attr('checked', true);
            $("option[value='" + this.model.get('conditionSelect') + "']").attr("selected", true);

            if (this.model.get('conditionType') === 'advanced') {
                $('#advEdit').prop('disabled','');
            } else {
                $('#advEdit').prop('disabled','disabled');
            }

            this.delegateEvents();
        }
        return this;
    }

}

export default MainConditionView;
